import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import CannedVegsFruitsProduct from 'models/CannedVegsFruitsProduct'
import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import Switch from 'react-switch'
import {
  Button,
  ButtonGroup,
  Col,
  FormGroup,
  Input,
  Row,
  Alert,
} from 'reactstrap'
import { useFormUtils } from 'views/shared/useFormUtils'
import {
  fruitProductOptions,
  fruitTypeOptions,
  packagingInitialState,
  productTypeOptions,
  vegProductOptions,
} from './constants'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const CannedVegsFruitsPackage = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const { setSelect, onChange, setSwitch, asterisk } = useFormUtils()

  const [packaging, setPackaging] = useState(packagingInitialState)

  const [productOptions, setProductOptions] = useState(fruitProductOptions)

  // Setup the "Product Type" select and "Type" select controls
  // based upon the current type of product
  const configureProductType = prodType => {
    const isFruitType = fruitTypeOptions.find(
      product => product.value === prodType
    )
    setProductOptions(isFruitType ? fruitProductOptions : vegProductOptions)
  }

  // When "Product Type" selection changed
  // also change the "Type" selection options
  const setProductType = selected => {
    setProductOptions(
      selected === 'Fruit' ? fruitProductOptions : vegProductOptions
    )
    setCurrent({
      ...current,
      ItemType: '',
      otherType: '',
      ContainerType: '',
      otherContainerType: '',
    })
  }

  const methods = useFormContext()

  const optionsReset = () => {
    methods.reset(
      {
        ItemType: '',
        otherType: '',
        packageType: '',
        ContainerType: '',
        otherContainerType: '',
        noAddtlIngredients: '',
      },
      {
        errors: false, // errors will not be reset
        dirtyFields: false, // dirtyFields will not be reset
        dirty: false, // dirty will not be reset
        isSubmitted: false,
        touched: false,
        isValid: false,
        submitCount: false,
      }
    )
  }

  useEffect(() => {
    setScrollToTop(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      current?.category === packagingInitialState.category &&
      current?.subCategory === packagingInitialState.subCategory
    ) {
      const {
        category,
        subCategory,
        ItemType,
        Size,
        ContainerType,
        packageType,
        Packaged,
        noAddtlIngredients,
        OrganicVerification,
        CannedProductType,
        otherType,
        otherContainerType,
      } = current
      setPackaging({
        category,
        subCategory,
        ItemType: {
          value: ItemType || '',
          label:
            ItemType.length > 0
              ? ItemType
              : productOptions.typeOptions[0].label,
        },
        Size,
        ContainerType: {
          value: ContainerType || '',
          label: ContainerType.length > 0 ? ContainerType : 'Container Type',
        },
        packageType: {
          value: packageType || '',
          label: packageType.length > 0 ? packageType : 'Package Type',
        },
        Packaged: {
          value: Packaged || '',
          label: Packaged.length > 0 ? Packaged : 'Package Size',
        },
        noAddtlIngredients,
        OrganicVerification,
        CannedProductType,
        otherType,
        otherContainerType,
      })
    } else if (!updating) {
      setCurrent(CannedVegsFruitsProduct)
      setProductOptions(fruitProductOptions)
      configureProductType('')
    }
    // eslint-disable-next-line
    if (current?.CannedProductType === 'Fruit') {
      setProductOptions(fruitProductOptions)
    } else {
      setProductOptions(vegProductOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  useEffect(() => {
    if (updating) {
      if (current?.ContainerType === 'Other') {
        methods.setValue('otherContainerType', current.otherContainerType)
      }
      if (current?.ItemType === 'Other') {
        methods.setValue('otherType', current.otherType)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  return (
    <Row>
      <Col xs={12} md={4} lg={4}>
        <p>
          <strong>Canned Product Type {asterisk}</strong>
        </p>
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            onClick={() => {
              optionsReset()
              setProductType(productTypeOptions[0])
              setCurrent({
                ...current,
                CannedProductType: productTypeOptions[0],
                packageType: '',
                ItemType: '',
                otherType: '',
                ContainerType: '',
                otherContainerType: '',
                noAddtlIngredients: '',
              })
            }}
            active={current?.CannedProductType === productTypeOptions[0]}
            disabled={
              updating && current?.CannedProductType !== productTypeOptions[0]
            }
            size="sm"
          >
            {productTypeOptions[0]}
          </Button>
        </ButtonGroup>
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            onClick={() => {
              optionsReset()
              setProductType(productTypeOptions[1])
              setCurrent({
                ...current,
                CannedProductType: productTypeOptions[1],
                packageType: '',
                ItemType: '',
                otherType: '',
                ContainerType: '',
                otherContainerType: '',
                noAddtlIngredients: '',
              })
            }}
            active={current?.CannedProductType === productTypeOptions[1]}
            disabled={
              updating && current?.CannedProductType !== productTypeOptions[1]
            }
            size="sm"
          >
            {productTypeOptions[1]}
          </Button>
        </ButtonGroup>
        <br />
        <br />
        <p>
          <strong>Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={productOptions.typeOptions}
          name="ItemType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Type'}
          onChange={([selected]) => {
            setSelect(selected, 'ItemType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="ItemType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        {current?.ItemType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="otherType"
              placeholder={'Other Type'}
              defautlValue={current?.otherType}
              control={methods.control}
              rules={{
                required: true,
                maxLength: {
                  value: 15,
                  message: '⚠ Other Type exceeds 15 characters.',
                },
              }}
              onKeyDown={e => {
                if (isNotAlpha(e.key)) {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="otherType"
                message={'Other type is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <FormGroup>
          <p>
            <strong>Size {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            name="Size"
            placeholder={'Size'}
            control={methods.control}
            rules={{
              required: true,
              maxLength: {
                value: 5,
                message: '⚠ Size exceeds 5 characters.',
              },
            }}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Size"
              message={'Size is required.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={12} md={4} lg={4}>
        <p>
          <strong>Container Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={productOptions.containerTypeOptions}
          name="ContainerType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Container Type'}
          onChange={([selected]) => {
            setSelect(selected, 'ContainerType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="ContainerType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        {current?.ContainerType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Container Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="otherContainerType"
              placeholder={'Other Container Type'}
              control={methods.control}
              rules={{
                required: true,
                maxLength: {
                  value: 15,
                  message: '⚠ Other Container Type exceeds 15 characters.',
                },
              }}
              defaultValue={current?.otherContainerType}
              onKeyDown={e => {
                if (isNotAlpha(e.key)) {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="otherContainerType"
                message={'Other container type is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <p>
          <strong>
            {productOptions.packTypeTitle} {asterisk}
          </strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={productOptions.packTypeOptions}
          name="packageType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={productOptions.packTypePlaceholder}
          onChange={([selected]) => {
            setSelect(selected, 'packageType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="packageType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        <p>
          <strong>Packaged {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={productOptions.packSizeOptions}
          name="Packaged"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Package Size'}
          onChange={([selected]) => {
            setSelect(selected, 'Packaged')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="Packaged"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
      </Col>
      <Col xs={12} md={4} lg={4}>
        <p>
          <strong>Organic</strong>
        </p>
        <Controller
          as={Switch}
          name="OrganicVerification"
          id="OrganicVerification"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.OrganicVerification}
        />
        <br />
        <hr />
        <p>
          <strong>
            {productOptions.noAdditionalIngredients} {asterisk}
          </strong>
        </p>
        <Controller
          as={Switch}
          name="noAddtlIngredients"
          id="noAddtlIngredients"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.noAddtlIngredients}
          rules={{
            validate: noAddtlIngredients => noAddtlIngredients === true,
          }}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="noAddtlIngredients"
            message={'⚠ Product must not have additional ingredients.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
      </Col>
    </Row>
  )
}

export default CannedVegsFruitsPackage
